import { useEffect, useState, useCallback } from "react";
import { Loader2, BarChart3, XCircle, Wallet } from "lucide-react";
import { Contract, JsonRpcProvider, formatEther } from "ethers";
import { useWallet } from "../contexts/WalletContext";
import { NFT_ADDRESS, MARKETPLACE_ADDRESS, PhygitalNFTAbi, NFTMarketplaceAbi, DEPLOYED_CHAIN_ID } from "../contracts";
import { fetchMetadata } from "../utils/ipfs";
import { POLYGON_RPC_URL } from "../config/web3Config";
import NFTImage from "../components/NFTImage";
import Toast from "../components/Toast";

interface ListedNFT { tokenId: bigint; price: bigint; name: string; description: string; image: string; isGenesis: boolean; }
interface ToastState { message: string; type: "success"|"error"|"info"|"pending"; txHash?: string; }

export default function DashboardPage() {
  const { isConnected, signer, account, chainId } = useWallet();
  const [nfts,      setNfts]      = useState<ListedNFT[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [canceling, setCanceling] = useState<bigint | null>(null);
  const [toast,     setToast]     = useState<ToastState | null>(null);

  const loadListings = useCallback(async () => {
    if (!account) return;
    try {
      setLoading(true);
      const provider = new JsonRpcProvider(POLYGON_RPC_URL);
      const nft = new Contract(NFT_ADDRESS, PhygitalNFTAbi, provider);
      const mkt = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, provider);
      const total: bigint = await nft.totalMinted();
      const items: ListedNFT[] = [];
      for (let id = 1n; id <= total; id++) {
        const listing = await mkt.getListing(NFT_ADDRESS, id) as { seller: string; price: bigint; active: boolean };
        if (!listing.active) continue;
        if (listing.seller.toLowerCase() !== account.toLowerCase()) continue;
        const uri: string = await nft.tokenURI(id);
        const meta = await fetchMetadata(uri);
        items.push({ tokenId: id, price: listing.price, name: meta.name,
          description: meta.description, image: meta.image, isGenesis: id === 1n });
      }
      setNfts(items);
    } catch (e) { console.error("loadListings:", e); }
    finally { setLoading(false); }
  }, [account]);

  useEffect(() => { if (isConnected && account) loadListings(); else setLoading(false); }, [isConnected, account, loadListings]);

  const cancelListing = async (tokenId: bigint) => {
    if (!signer) return;
    try {
      setCanceling(tokenId);
      setToast({ message: "Cancelling listing…", type: "pending" });
      const mktW = new Contract(MARKETPLACE_ADDRESS, NFTMarketplaceAbi, signer);
      const tx = await mktW.delistNFT(NFT_ADDRESS, tokenId);
      setToast({ message: "Submitted…", type: "pending", txHash: tx.hash });
      await tx.wait();
      setToast({ message: "Listing cancelled!", type: "success", txHash: tx.hash });
      loadListings();
    } catch (e: any) {
      setToast({ message: e?.reason ?? e?.message ?? "Cancel failed", type: "error" });
    } finally { setCanceling(null); }
  };

  if (!isConnected) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Connect Your Wallet</h2>
        <p className="text-gray-500">Connect to view your active listings</p>
      </div>
    </div>
  );
  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading your listings…</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {toast && <Toast {...toast} chainId={chainId || DEPLOYED_CHAIN_ID} onClose={() => setToast(null)} />}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">Dashboard</h1>
        <p className="text-gray-500">Your active marketplace listings</p>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-8 flex items-center justify-between">
        <div>
          <p className="text-sm text-blue-500 font-medium">Active Listings</p>
          <p className="text-3xl font-bold text-blue-900">{nfts.length}</p>
        </div>
        <BarChart3 className="w-10 h-10 text-blue-400" />
      </div>

      {nfts.length === 0 ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No active listings — mint or list an NFT to get started!</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {nfts.map(nft => (
            <div key={nft.tokenId.toString()}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {nft.isGenesis && (
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold text-center py-1 tracking-widest uppercase">
                  ◆ Genesis NFT
                </div>
              )}
              <NFTImage tokenId={Number(nft.tokenId)} image={nft.image} name={nft.name} />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 truncate mb-0.5">{nft.name}</h3>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">nftmarketplace.ca</p>
                <div className="mb-4">
                  <p className="text-xs text-gray-400">Listed Price</p>
                  <p className="text-lg font-bold text-blue-600">{parseFloat(formatEther(nft.price)).toFixed(4)} POL</p>
                </div>
                <button onClick={() => cancelListing(nft.tokenId)} disabled={canceling === nft.tokenId}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {canceling === nft.tokenId
                    ? <><Loader2 className="w-4 h-4 animate-spin" />Cancelling…</>
                    : <><XCircle className="w-4 h-4" />Cancel Listing</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
